import React from 'react';
import { ReducerState } from '@/services/common/lib/reducerState';
import { useDispatch, useSelector } from 'react-redux';
import { PokerCategory } from '@/services/common/types/pokerCategory';
import CardFace, { CardSizes } from '@/components/CardFace/CardFace';
import { checkWinningCard } from '../../utils/checkWinningCard';
import { motion } from 'framer-motion';
import { TableState } from '@/services/table/tableState';
import { twMerge } from 'tailwind-merge';
import { addDiscardCardAction, removeDiscardCardAction } from '@/services/table/tableActions';

interface PlayerCardsProps {
  tableId: string;
  tableState: TableState;
  cardAreFanned?: boolean;
}

export const overlapMargins = {
  [CardSizes.xxs]: 'ml-3',
  [CardSizes.xs]: 'ml-4',
  [CardSizes.sm]: 'ml-7',
  [CardSizes.md]: 'ml-9',
  [CardSizes.lg]: 'ml-12',
  [CardSizes.xl]: 'ml-[3.25em]',
};
const PlayerCards: React.FC<PlayerCardsProps> = ({ tableId, tableState, cardAreFanned }) => {
  const user = useSelector((state: ReducerState) => state.auth);

  const player = useSelector((state: ReducerState) => state.players[tableId]);

  const playerSeat = tableState.playerSeat;

  const dispatch = useDispatch();
  const cardIndexesForDiscard = player?.cardIndexesForDiscard;

  const isTexasVariant = tableState?.currentVariant?.category === PokerCategory.HOLDEM;
  const isDrawVariant = tableState?.currentVariant?.category === PokerCategory.DRAW;

  const showFoldedCards =
    user.settings?.displayFoldedCardsEnabled && playerSeat?.foldedCards && playerSeat?.foldedCards?.length > 0;

  const isDiscardCard = (cardIndex: number) => {
    return cardIndexesForDiscard && cardIndexesForDiscard.includes(cardIndex);
  };

  const onClickCardToDiscard = (cardIndex: number) => {
    isDiscardCard(cardIndex)
      ? dispatch(removeDiscardCardAction(tableId, cardIndex))
      : dispatch(addDiscardCardAction(tableId, cardIndex));
  };

  const shouldDisplayCards = !!tableState?.handState?.state?.statuses[playerSeat?.playerIndex!] || showFoldedCards;

  const cardSize = CardSizes.xl;

  return (
    <div
      className={twMerge(
        'flex flex-row justify-center gap-1',
        !isTexasVariant && shouldDisplayCards ? overlapMargins[cardSize] : '',
      )}
    >
      {shouldDisplayCards &&
        showFoldedCards &&
        playerSeat?.foldedCards.map((card, handCardIndex) => (
          <CardFace
            size={cardSize}
            key={`option-${card.rank}${card.suit}-${handCardIndex}`}
            rank={card.rank}
            suit={card.suit}
            isOverlap={!isTexasVariant}
            isDiscarded
            handCardIndex={handCardIndex}
          />
        ))}
      {shouldDisplayCards &&
        !showFoldedCards &&
        tableState?.handState?.handId !== undefined &&
        !!playerSeat &&
        playerSeat?.cardIndexes?.map((deckCardIndex: number, handCardIndex: number) => {
          const isCardVisible = tableState?.handState?.state?.all_hole_dealing_statuses[handCardIndex];

          const card = playerSeat.holeCards[handCardIndex];

          if (!card) {
            return null;
          }

          const isWinnerCard = tableState?.winnersData?.cards && checkWinningCard(card, tableState?.winnersData?.cards);

          return (
            <motion.div
              key={`option-${card.rank}${card.suit}-${handCardIndex}`}
              initial={{ y: -100, rotate: -75, zoom: 1.2 }}
              animate={{ y: 0, zoom: isWinnerCard ? 1.1 : 1, rotate: 0 }}
              transition={{ duration: isWinnerCard ? 0 : 0.6 }}
              style={{
                zIndex: handCardIndex + 1,
              }}
            >
              <CardFace
                onClick={isDrawVariant ? () => onClickCardToDiscard(deckCardIndex) : undefined}
                size={cardSize}
                key={`option-${card.rank}${card.suit}-${handCardIndex}`}
                rank={card.rank}
                suit={card.suit}
                isWinner={isWinnerCard}
                isOverlap={!isTexasVariant}
                isElevated={isCardVisible}
                isDiscarded={isDiscardCard(deckCardIndex)}
                handCardIndex={handCardIndex}
                cardAreFanned={!isTexasVariant && cardAreFanned}
                totalCards={playerSeat.cardIndexes.length}
              />
            </motion.div>
          );
        })}
    </div>
  );
};

export default PlayerCards;
